<template>
  <DefaultModal
    ref="modal"
    :title="$t('generate_approval_link')"
    :loading="loading"
    :save-button-disabled="!hasEmails || dateIsInvalid"
    submit-button-text="send"
    max-width="550px"
    @submit="save"
  >
    <template #content>
      <UserEmailTextarea
        :key="formKey"
        v-model="emails"
        :messages="$t('message_email_separeted_comma')"
        :rules="emailRules"
      />

      <p class="subtitle mt-5">{{ $t('link_expiration_datetime') }}</p>
      <v-row>
        <v-col cols="12" sm="6">
          <v-menu
            ref="datePickerMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            @input="onDatePickerMenuChange"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="formattedExpirationDate"
                :label="$t('date')"
                prepend-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="tempExpirationDate"
              :min="startDate"
              :locale="currentLocale"
              no-title
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="cancelExpirationDate"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="saveExpirationDate"
              >
                {{ $t('ok') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            ref="timeMenu"
            v-model="timeMenu"
            :close-on-content-click="false"
            :return-value.sync="expirationTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="expirationTime"
                :label="$t('hour')"
                prepend-icon="mdi-clock-time-four-outline"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-time-picker
              v-if="timeMenu"
              v-model="expirationTime"
              full-width
              @click:minute="$refs.timeMenu.save(expirationTime)"
            />
          </v-menu>
        </v-col>
      </v-row>

      <v-alert v-if="dateIsInvalid" dense type="error" outlined>
        {{ $t('error_date_less_than_current') }}
      </v-alert>
    </template>
  </DefaultModal>
</template>

<script>
import { mapActions } from 'vuex'
import DefaultModal from '@/components/DefaultModal.vue'
import UserEmailTextarea from '@/components/core/UserEmailTextarea.vue'
import JobAPI from '@/services/api/job/job'
import AppActions from '@/store/app/actions-types'
import NotificationActions from '@/store/core/notification/actions-types'

export default {
  name: 'ApprovalLinkModal',

  components: {
    DefaultModal,
    UserEmailTextarea
  },

  props: {
    jobId: { type: [String, Number], required: true },
    jobTitle: { type: String, default: '' },
    jobAppId: { type: [String, Number], required: true },
    contentAppId: { type: [String, Number], required: true },
    businessAppId: { type: [String, Number], required: true }
  },

  data() {
    return {
      loading: false,
      emails: [],
      expirationDate: null,
      tempExpirationDate: null,
      expirationTime: null,
      startDate: null,
      dateMenu: false,
      timeMenu: false,
      formKey: 0
    }
  },

  computed: {
    recipientEmails() {
      return this.emails.filter(email => email && email.trim())
    },

    hasEmails() {
      return this.recipientEmails.length > 0
    },

    expirationDateTime() {
      if (!this.expirationDate || !this.expirationTime) return null

      return new Date(`${this.expirationDate}T${this.expirationTime}:00`)
        .toISOString()
        .replace(/\.\d{3}Z$/, 'Z')
    },

    formattedExpirationDate() {
      if (!this.expirationDate) return ''

      const [year, month, day] = this.expirationDate.split('-')
      return `${day}/${month}/${year}`
    },

    currentLocale() {
      return this.$i18n.locale
    },

    dateIsInvalid() {
      return !this.expirationDateTime || Date.parse(this.expirationDateTime) <= Date.now()
    },

    emailRules() {
      return [
        value => !!(value && value.trim()) || this.$t('form_rules.mandatory'),
        value => this.areEmailsValid(value) || this.$t('message_invalid_email')
      ]
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),
    ...mapActions('core/notification', [
      NotificationActions.SEND_SHARE_MESSAGE
    ]),

    open() {
      const now = new Date()
      this.startDate = now.toISOString().substr(0, 10)
      this.expirationDate = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000) - (now.getTimezoneOffset() * 60000))
        .toISOString()
        .substr(0, 10)
      this.expirationTime = now.toLocaleTimeString(undefined, { hour12: false }).slice(0, -3)
      this.emails = []
      this.formKey++
      this.$refs.modal.openModal()
    },

    async save() {
      if (!this.hasEmails || this.dateIsInvalid) return

      this.loading = true
      try {
        const response = await JobAPI.createExternalMarkupLinks(
          this.jobId,
          this.recipientEmails,
          this.expirationDateTime
        )
        const links = this.normalizeLinks(response.data)

        if (!links.length) {
          throw new Error(this.$t('approval_link_generation_error'))
        }

        await Promise.all(links.map(({ email, token }) => this.sendApprovalEmail(email, token)))
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('send_mail_successfully'))
        this.$refs.modal.closeModal()
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || error.message || this.$t('approval_link_generation_error'))
      } finally {
        this.loading = false
        this.$refs.modal.submitting = false
      }
    },

    normalizeLinks(data) {
      const links = Array.isArray(data) ? data : (data?.items || data?.data || [])
      return links.filter(link => link?.email && link?.token)
    },

    areEmailsValid(value) {
      if (!value) return true

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return value
        .split(/[\s,]+/)
        .filter(Boolean)
        .every(email => emailPattern.test(email))
    },

    onDatePickerMenuChange(isOpen) {
      if (isOpen) {
        this.tempExpirationDate = this.expirationDate
      }
    },

    cancelExpirationDate() {
      this.dateMenu = false
    },

    saveExpirationDate() {
      this.expirationDate = this.tempExpirationDate
      this.dateMenu = false
    },

    publicLink(token) {
      const href = this.$router.resolve({
        name: 'PublicDeliverableAssets',
        params: {
          appId: this.jobAppId,
          jobId: this.jobId,
          contentAppId: this.contentAppId,
          businessAppId: this.businessAppId
        },
        query: {
          'scoped-token': token
        }
      }).href

      return new URL(href, window.location.origin).toString()
    },

    sendApprovalEmail(email, token) {
      const link = this.publicLink(token)
      const title = this.jobTitle || this.$t('deliverable_assets')
      const sharedBy = this.$store.state.core.auth.userData?.name || this.$t('user')

      return this[NotificationActions.SEND_SHARE_MESSAGE]({
        emails: [email],
        subject: this.$t('approval_link_email_subject', { jobTitle: title }),
        content: this.$t('approval_link_email_content', {
          sharedBy,
          jobTitle: title,
          link
        })
      })
    }
  }
}
</script>

<style scoped>
.subtitle {
  font-size: 16px;
  color: rgb(60, 64, 67);
}
</style>
